import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete as DeleteIcon } from '@mui/icons-material';
import { shopTaskService } from '../services/shopTaskService';
import { workFunctionService } from '../services/workFunctionService';
import { formatDateOnly } from '../utils/dateUtils';
import { taskStatusChip } from './ShopTasks';

const EMPTY_FORM = {
  name: '',
  work_order_id: null,
  work_function_ids: [],
  admin_notes: '',
  shop_notes: '',
  status: 'open',
};

const sortedIds = (ids) => [...ids].map(Number).sort((a, b) => a - b);

function taskToForm(task) {
  return {
    name: task.name || '',
    work_order_id: task.work_order_id || null,
    work_function_ids: sortedIds(task.work_function_ids || []),
    admin_notes: task.admin_notes || '',
    shop_notes: task.shop_notes || '',
    status: task.status || 'open',
  };
}

function orderLabel(o) {
  if (!o) return '';
  const id = o.identifier || o.work_order_number || `#${o.id}`;
  return o.customer_name ? `${id} — ${o.customer_name}` : String(id);
}

function orderSecondary(o) {
  return [o.quote_identifier && `Quote ${o.quote_identifier}`, o.po_number && `PO ${o.po_number}`]
    .filter(Boolean)
    .join(' · ');
}

const filterOrders = (options, { inputValue }) => {
  const q = inputValue.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) =>
    [o.identifier, o.work_order_number, o.customer_name, o.quote_identifier, o.po_number]
      .some((v) => v != null && String(v).toLowerCase().includes(q))
  );
};

const ShopTaskDetail = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get('work_order_id');

  const [task, setTask] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [orders, setOrders] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isDefault = !!task?.is_default;
  const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  const applyTask = useCallback((row) => {
    setTask(row);
    const next = taskToForm(row);
    setForm(next);
    setSavedForm(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wfRes, orderRes] = await Promise.all([
          workFunctionService.getAll(),
          shopTaskService.getOrderOptions(presetOrderId || undefined),
        ]);
        if (cancelled) return;
        setFunctions(wfRes.data || []);
        setOrders(orderRes.data || []);
      } catch {
        /* lookups optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presetOrderId]);

  useEffect(() => {
    if (isNew) {
      const next = { ...EMPTY_FORM, work_order_id: presetOrderId ? parseInt(presetOrderId, 10) : null };
      setTask(null);
      setForm(next);
      setSavedForm(EMPTY_FORM);
      return;
    }
    let cancelled = false;
    setLoading(true);
    shopTaskService
      .getById(id)
      .then((res) => {
        if (!cancelled) applyTask(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load task');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, presetOrderId, applyTask]);

  const orderOptions = useMemo(() => {
    const list = [...orders];
    if (task?.work_order_id && !list.some((o) => o.id === task.work_order_id)) {
      list.unshift({
        id: task.work_order_id,
        identifier: task.work_order_identifier,
        work_order_number: task.work_order_number,
        customer_name: task.customer_name,
      });
    }
    return list;
  }, [orders, task]);

  const functionOptions = useMemo(() => {
    const list = [...functions];
    for (const f of task?.functions || []) {
      if (!list.some((x) => x.id === f.id)) list.push(f);
    }
    return list;
  }, [functions, task]);

  const selectedOrder = orderOptions.find((o) => o.id === form.work_order_id) || null;
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleFunction = (fnId) =>
    setForm((prev) => {
      const has = prev.work_function_ids.includes(fnId);
      const next = has ? prev.work_function_ids.filter((x) => x !== fnId) : [...prev.work_function_ids, fnId];
      return { ...prev, work_function_ids: sortedIds(next) };
    });

  const handleSave = async ({ close } = {}) => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      const res = isNew ? await shopTaskService.create(payload) : await shopTaskService.update(task.id, payload);
      applyTask(res.data);
      if (close) navigate('/shop-tasks');
      else if (isNew) navigate(`/shop-tasks/${res.data.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task || !window.confirm(`Delete task "${task.label}"? This cannot be undone.`)) return;
    try {
      await shopTaskService.delete(task.id);
      navigate('/shop-tasks');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete task');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const byEmployee = task?.hours_by_employee || [];
  const byFunction = task?.hours_by_function || [];
  const canDelete = task && !isDefault && Number(task.hours_logged || 0) === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/shop-tasks')} sx={{ textTransform: 'none' }}>
          Tasks
        </Button>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isNew ? 'New Task' : `Task / ${task?.label || id}`}
          </Typography>
          {task && taskStatusChip(task)}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ textTransform: 'none' }}>
              Delete
            </Button>
          )}
          <Button onClick={() => navigate('/shop-tasks')} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="outlined" disabled={saving || (!dirty && !isNew)} onClick={() => handleSave()}>
            Save
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => handleSave({ close: true })}>
            Save & Close
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {isDefault && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is the default task. Every clock-in starts here unless the employee picks another task, and any
          hours not allocated to a specific task land here.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Task Details" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={orderOptions}
                    value={selectedOrder}
                    disabled={isDefault}
                    getOptionLabel={orderLabel}
                    filterOptions={filterOrders}
                    renderOption={(props, o) => (
                      <li {...props} key={o.id}>
                        <Box>
                          <Typography variant="body2">{orderLabel(o)}</Typography>
                          {orderSecondary(o) ? (
                            <Typography variant="caption" color="text.secondary">{orderSecondary(o)}</Typography>
                          ) : null}
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_e, v) => setField('work_order_id', v ? v.id : null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Order" size="small" placeholder="Order, quote, PO, or customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset" disabled={isDefault}>
                    <FormLabel component="legend" sx={{ fontSize: '0.8rem' }}>Functions</FormLabel>
                    <FormGroup row>
                      {functionOptions.map((wf) => (
                        <FormControlLabel
                          key={wf.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.work_function_ids.includes(wf.id)}
                              onChange={() => toggleFunction(wf.id)}
                            />
                          }
                          label={wf.label}
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText sx={{ mx: 0 }}>
                      {form.work_function_ids.length > 1
                        ? 'Employees pick one of these functions when they clock into this task.'
                        : 'Check every function employees can do on this task.'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description (optional)"
                    placeholder="e.g. Frame weldment"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    helperText="Shown after the order and function on the kiosk."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth margin="dense" size="small" disabled={isDefault}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={form.status} onChange={(e) => setField('status', e.target.value)}>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    label="Shop Notes"
                    value={form.shop_notes}
                    onChange={(e) => setField('shop_notes', e.target.value)}
                    helperText="Visible to employees on the kiosk when they pick this task."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    label="Admin Notes"
                    value={form.admin_notes}
                    onChange={(e) => setField('admin_notes', e.target.value)}
                    helperText="Office only. Never shown on the kiosk."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {!isNew && (
          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader
                title="Hours Logged"
                subheader={`${Number(task?.hours_logged || 0).toFixed(2)} h total`}
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
              <CardContent>
                {task?.work_order_id && (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Order:{' '}
                    <Link component={RouterLink} to={`/work-orders/${task.work_order_id}`}>
                      {task.work_order_identifier || task.work_order_number}
                    </Link>
                    {task.customer_name ? ` — ${task.customer_name}` : ''}
                  </Typography>
                )}
                {isDefault ? (
                  <Typography variant="body2" color="text.secondary">
                    Unallocated hours from every closed punch since this task was created.
                  </Typography>
                ) : byEmployee.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No hours yet. Employees can pick this task when clocking in, or a manager can allocate hours
                    from Labor Allocation.
                  </Typography>
                ) : (
                  <>
                  {byFunction.length > 1 && (
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Function</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell align="right">People</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {byFunction.map((r) => (
                          <TableRow key={r.work_function_id ?? 'none'}>
                            <TableCell>{r.function_label}</TableCell>
                            <TableCell align="right">{Number(r.hours).toFixed(2)}</TableCell>
                            <TableCell align="right">{r.employee_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Punches</TableCell>
                        <TableCell>Last worked</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byEmployee.map((r) => (
                        <TableRow key={r.employee_id}>
                          <TableCell>{r.employee_name || `#${r.employee_id}`}</TableCell>
                          <TableCell align="right">{Number(r.hours).toFixed(2)}</TableCell>
                          <TableCell align="right">{r.punch_count}</TableCell>
                          <TableCell>{formatDateOnly(r.last_worked) || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ShopTaskDetail;
